import { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "convex/react";
import { useAuth } from "@clerk/clerk-react";
import { api } from "../../api";
import { ConvexHttpClient } from "convex/browser";
import { getCurrentConvexUrl } from "@/config/deployments";

// Wildcard app ID - URLs added to any app should also be added here
const WILDCARD_APP_ID = "j57fgp9z6fejkw5frve4bnx1957m90gc";

interface AppData {
  id: string;
  name: string;
  webkit: string[];
}

interface BulkUrlImportResult {
  bundleId: string;
  appName?: string;
  appId?: string;
  urlsAdded: number;
  urlsSkipped: number;
  status: 'success' | 'partial' | 'not_found' | 'error';
  message: string;
}

interface BulkUrlImportProgress {
  processed: number;
  total: number;
  phase: 'idle' | 'loading_catalog' | 'processing';
}

interface CsvRow {
  bundleId: string;
  urls: string[];
}

export const useBulkAppUrlImport = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<BulkUrlImportProgress>({ processed: 0, total: 0, phase: 'idle' });
  const [results, setResults] = useState<BulkUrlImportResult[]>([]);
  const { toast } = useToast();
  const { getToken } = useAuth();
  const updateWebkit = useMutation(api.apps.update.webkit);

  // Parse CSV content into structured rows
  const parseCsvContent = useCallback((content: string): CsvRow[] => {
    const lines = content.split('\n').filter(line => line.trim().length > 0);
    const rows: CsvRow[] = [];

    for (const line of lines) {
      // Skip header rows
      if (line.toLowerCase().includes('bundle') && line.toLowerCase().includes('url')) {
        continue;
      }

      let bundleId = '';
      let urls: string[] = [];

      // Try to detect format
      // Format 1: bundleId,url1,url2,url3
      // Format 2: bundleId,"[""url1"",""url2""]"
      // Format 3: bundleId,"url1,url2,url3"

      // Check for JSON array format first
      const jsonMatch = line.match(/^([^,]+),\s*"?\[(.+)\]"?$/);
      if (jsonMatch) {
        bundleId = jsonMatch[1].trim();
        try {
          // Parse JSON array
          const jsonStr = `[${jsonMatch[2].replace(/""/g, '"')}]`;
          urls = JSON.parse(jsonStr);
        } catch {
          // Fallback: split by comma
          urls = jsonMatch[2].split(',').map(u => u.replace(/["']/g, '').trim());
        }
      } else {
        // Split by comma, first element is bundleId, rest are URLs
        const parts = line.split(',').map(p => p.trim());
        bundleId = parts[0];
        urls = parts.slice(1).filter(u => u.length > 0);
      }

      if (bundleId && urls.length > 0) {
        // Normalize URLs: lowercase and trim
        const normalizedUrls = urls.map(url => 
          url.replace(/^["']|["']$/g, '').trim().toLowerCase()
        ).filter(url => url.length > 0);

        if (normalizedUrls.length > 0) {
          rows.push({ bundleId, urls: normalizedUrls });
        }
      }
    }

    return rows;
  }, []);

  // Load ALL apps from catalog with pagination
  const loadAllApps = useCallback(async (): Promise<Map<string, AppData>> => {
    const convexUrl = getCurrentConvexUrl();
    const client = new ConvexHttpClient(convexUrl);
    
    // Get Clerk auth token and set it on the client
    const token = await getToken({ template: "convex" });
    if (token) {
      client.setAuth(token);
    }
    
    const appMap = new Map<string, AppData>();
    let cursor: string | null = null;
    let totalLoaded = 0;

    // Paginate through ALL apps (no search query)
    do {
      const result = await client.query(api.apps.list.all, {
        paginationOpts: { cursor, numItems: 100 }
      });

      // Fetch webkit URLs for this batch
      const appIds = result.page.map((app: any) => app.id);
      if (appIds.length > 0) {
        const appsData = await client.query(api.apps.get.apps, { apps: appIds });

        // Build lookup map: bundleId -> app data
        result.page.forEach((listApp: any, idx: number) => {
          const detailApp = appsData[idx];
          if (listApp.bundleId) {
            appMap.set(listApp.bundleId, {
              id: listApp.id,
              name: listApp.name,
              webkit: (detailApp as any)?.webkit || []
            });
          }
        });

        totalLoaded += appIds.length;
        setProgress(prev => ({ ...prev, processed: totalLoaded }));
      }

      cursor = result.isDone ? null : result.continueCursor;
    } while (cursor);

    console.log(`[BulkUrlImport] Loaded ${appMap.size} apps from catalog`);
    return appMap;
  }, [getToken]);

  // Get current wildcard app URLs
  const getWildcardUrls = useCallback(async (): Promise<string[]> => {
    try {
      const convexUrl = getCurrentConvexUrl();
      const client = new ConvexHttpClient(convexUrl);
      
      // Get Clerk auth token and set it on the client
      const token = await getToken({ template: "convex" });
      if (token) {
        client.setAuth(token);
      }
      
      const result = await client.query(api.apps.get.apps, { apps: [WILDCARD_APP_ID as any] });
      return (result?.[0] as any)?.webkit || [];
    } catch {
      return [];
    }
  }, [getToken]);

  const importUrls = async (csvContent: string) => {
    setIsImporting(true);
    setResults([]);

    try {
      // Phase 1: Load all apps from catalog
      setProgress({ processed: 0, total: 0, phase: 'loading_catalog' });
      toast({
        title: "Loading app catalog...",
        description: "Fetching all apps from the catalog",
      });

      const appMap = await loadAllApps();
      console.log(`[BulkUrlImport] Catalog loaded with ${appMap.size} apps`);

      // Phase 2: Parse CSV
      const rows = parseCsvContent(csvContent);
      
      if (rows.length === 0) {
        toast({
          title: "No valid data",
          description: "No valid bundle ID and URL pairs found in the input.",
          variant: "destructive",
        });
        setIsImporting(false);
        setProgress({ processed: 0, total: 0, phase: 'idle' });
        return;
      }

      // Phase 3: Process each row
      setProgress({ processed: 0, total: rows.length, phase: 'processing' });
      toast({
        title: "Processing URLs...",
        description: `Processing ${rows.length} bundle IDs`,
      });

      const importResults: BulkUrlImportResult[] = [];
      let wildcardUrls = await getWildcardUrls();
      const newWildcardUrls = new Set(wildcardUrls);

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];

        try {
          // Use the pre-loaded map for O(1) lookup
          const app = appMap.get(row.bundleId);

          if (!app) {
            importResults.push({
              bundleId: row.bundleId,
              urlsAdded: 0,
              urlsSkipped: row.urls.length,
              status: 'not_found',
              message: 'App not found in catalog'
            });
          } else {
            // Merge URLs, avoiding duplicates
            const existingUrls = new Set(app.webkit);
            const urlsToAdd: string[] = [];
            let skipped = 0;

            for (const url of row.urls) {
              if (existingUrls.has(url)) {
                skipped++;
              } else {
                urlsToAdd.push(url);
                existingUrls.add(url);
              }
            }

            if (urlsToAdd.length > 0) {
              // Update app webkit URLs
              const newWebkit = [...app.webkit, ...urlsToAdd];
              await updateWebkit({
                app: app.id as any,
                webkit: newWebkit
              });

              // Also add to wildcard app (unless this IS the wildcard)
              if (app.id !== WILDCARD_APP_ID) {
                for (const url of urlsToAdd) {
                  newWildcardUrls.add(url);
                }
              }

              // Update local cache for subsequent lookups
              app.webkit = newWebkit;
            }

            importResults.push({
              bundleId: row.bundleId,
              appName: app.name,
              appId: app.id,
              urlsAdded: urlsToAdd.length,
              urlsSkipped: skipped,
              status: urlsToAdd.length > 0 ? (skipped > 0 ? 'partial' : 'success') : 'partial',
              message: urlsToAdd.length > 0
                ? `Added ${urlsToAdd.length} URL(s)${skipped > 0 ? `, ${skipped} already existed` : ''}`
                : `All ${skipped} URL(s) already existed`
            });
          }
        } catch (error) {
          console.error('Error processing row:', row.bundleId, error);
          importResults.push({
            bundleId: row.bundleId,
            urlsAdded: 0,
            urlsSkipped: row.urls.length,
            status: 'error',
            message: error instanceof Error ? error.message : 'Failed to update'
          });
        }

        setResults([...importResults]);
        setProgress({ processed: i + 1, total: rows.length, phase: 'processing' });
      }

      // Update wildcard app with all new URLs
      const wildcardUrlsToAdd = Array.from(newWildcardUrls).filter(url => !wildcardUrls.includes(url));
      if (wildcardUrlsToAdd.length > 0) {
        try {
          await updateWebkit({
            app: WILDCARD_APP_ID as any,
            webkit: Array.from(newWildcardUrls)
          });
          console.log(`[BulkUrlImport] Added ${wildcardUrlsToAdd.length} URLs to wildcard app`);
        } catch (error) {
          console.error('Failed to update wildcard app:', error);
        }
      }

      const successCount = importResults.filter(r => r.status === 'success' || r.status === 'partial').length;
      const notFoundCount = importResults.filter(r => r.status === 'not_found').length;
      const errorCount = importResults.filter(r => r.status === 'error').length;

      toast({
        title: "Import Complete",
        description: `Processed ${rows.length} apps: ${successCount} updated, ${notFoundCount} not found, ${errorCount} errors`,
      });
    } catch (error) {
      console.error('Import failed:', error);
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to import URLs",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
      setProgress(prev => ({ ...prev, phase: 'idle' }));
    }
  };

  const reset = () => {
    setProgress({ processed: 0, total: 0, phase: 'idle' });
    setResults([]);
    setIsImporting(false);
  };

  return {
    importUrls,
    parseCsvContent,
    isImporting,
    progress,
    results,
    reset
  };
};
